import { z } from 'zod';
import { objectIdSchema } from '../../validation';
import {
  FinancingApplicationStatus,
  financingApplicationStatusSchema,
} from '../financing/financing.enums';

/**
 * Coarse 4-value lifecycle of the invoice ↔ FinancingApplication
 * back-link. Distinct from the 9-value
 * {@link financingApplicationStatusSchema} so the invoice surface
 * can branch on intent ("can this invoice be financed?") without
 * leaking lender-flow detail.
 *
 * Mapping is centralised in
 * {@link mapFinancingApplicationStatusToInvoice} so adding a new
 * application status only needs one update.
 *
 * - `pending`   — application is in-flight (under review, sent to
 *   lenders, accepted by carrier, awaiting repayment approval).
 * - `financed`  — money has moved (funded, repaid, defaulted —
 *   regardless of how the loan ended, the invoice has been
 *   financed and can never be financed a second time).
 * - `rejected`  — lender declined the application. Used for audit;
 *   re-application on the same invoice is blocked.
 * - `cancelled` — admin- or carrier-initiated cancel before any
 *   money moved.
 */
export const invoiceFinancingStatusSchema = z.enum([
  'pending',
  'financed',
  'rejected',
  'cancelled',
]);

export type InvoiceFinancingStatus = z.infer<
  typeof invoiceFinancingStatusSchema
>;

/**
 * Back-link on `Invoice` recording which `FinancingApplication`
 * targeted it. Presence of this sub-doc is the single source of
 * truth for "this invoice is already in flight or has been
 * financed" — the `financeableOnly` query filters on
 * `{ $exists: false }` and the frontend's `canFinance` derived
 * flag flips off the moment this field appears.
 */
export const invoiceFinancingSchema = z.object({
  /** `FinancingApplication._id` that targeted this invoice. */
  _id: objectIdSchema,
  /** Coarse lifecycle — see {@link invoiceFinancingStatusSchema}. */
  status: invoiceFinancingStatusSchema,
});

export type InvoiceFinancing = z.infer<typeof invoiceFinancingSchema>;

/**
 * Map an internal 9-value FinancingApplication status to the
 * 4-value invoice-facing status. Single source of truth — callers
 * (backend sync helper, frontend helpers) MUST go through here so
 * a new application status is a one-line change.
 */
export function mapFinancingApplicationStatusToInvoice(
  status: FinancingApplicationStatus,
): InvoiceFinancingStatus {
  switch (status) {
    case 'pending_review':
    case 'submitted':
    case 'accepted':
    case 'awaiting_repayment_approval':
      return 'pending';
    case 'funded':
    case 'repaid':
    case 'defaulted':
      return 'financed';
    case 'declined':
      return 'rejected';
    case 'cancelled':
      return 'cancelled';
  }
}
